import { CalendarIcon, CopyIcon, DownloadIcon, TimeIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Tag,
    Text,
    VStack,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import Datepicker from "App/BsesPoc/components/common/DatePicker";
import { AppContext } from "AppProvider/AppContext";
import { useContext, useState } from "react";
import { appRequest } from "routes";

const DownloadReportElement = () => {
    const { accountDetails, workFlowData, toastNotify, startDate, endDate } =
        useContext(AppContext);
    const [reportLoading, setReportLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Local state for modal filters
    const [localStartDate, setLocalStartDate] = useState<Date | null>(startDate);
    const [localEndDate, setLocalEndDate] = useState<Date | null>(endDate);
    const [activeChip, setActiveChip] = useState<string | null>(null);

    const [isPendingToSubmit, setIsPendingToSubmit] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);

    const handleOpen = () => {
        setLocalStartDate(startDate);
        setLocalEndDate(endDate);
        setIsPendingToSubmit(false);
        setIsDuplicate(false);
        setActiveChip(null);
        onOpen();
    };

    const handleDateChipClick = (range: "today" | "lastWeek" | "lastMonth") => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let start = new Date(today);
        let end = new Date(today);

        if (range === "today") {
        } else if (range === "lastWeek") {
            start.setDate(today.getDate() - 7);
        } else if (range === "lastMonth") {
            start.setMonth(today.getMonth() - 1);
        }

        setLocalStartDate(start);
        setLocalEndDate(end);
        setActiveChip(range);
    };

    const validateDateRange = () => {
        if (!localStartDate || !localEndDate) return true;

        // Clone dates and strip time to compare just the calendar days
        const start = new Date(localStartDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(localEndDate);
        end.setHours(0, 0, 0, 0);

        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays <= 800;
    };

    const downloadExcelReport = async () => {
        if (localStartDate && localEndDate && !validateDateRange()) {
            toastNotify({
                type: "error",
                message: "Date range cannot exceed 1 month.",
            });
            return;
        }

        try {
            setReportLoading(true);

            const response: any = await appRequest(
                "document",
                "downloadOverAllExcelReport",
                {
                    startDate: localStartDate,
                    endDate: localEndDate,
                    status: undefined, // Status option removed from UI
                    level: accountDetails?.level,
                    processName: workFlowData?.processName,
                    processType: workFlowData?.processType,
                    isPendingToSubmit,
                    isDuplicate,
                }
            );

            if (response?.data?.base64 && response?.data?.fileName) {
                const byteCharacters = atob(response.data?.base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = response.data?.fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                onClose();
            }
        } catch (err: any) {
            toastNotify({
                type: "error",
                message: err?.message,
            });
        } finally {
            setReportLoading(false);
        }
    };

    // Colors
    const modalBg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const accentColor = "teal.500";

    return (
        <>
            <Box textAlign="center" my={4}>
                <Button
                    colorScheme="teal"
                    variant="solid"
                    size="lg"
                    leftIcon={<DownloadIcon />}
                    borderRadius="full"
                    px={8}
                    py={6}
                    fontSize="lg"
                    fontWeight="bold"
                    shadow="xl"
                    bgGradient="linear(to-r, teal.400, teal.500, teal.600)"
                    _hover={{
                        bgGradient: "linear(to-r, teal.500, teal.600, teal.700)",
                        shadow: "2xl",
                        transform: "translateY(-2px)",
                    }}
                    _active={{
                        bgGradient: "linear(to-r, teal.600, teal.700, teal.800)",
                        shadow: "md",
                        transform: "translateY(0)",
                    }}
                    transition="all 0.3s ease"
                    onClick={handleOpen}
                >
                    Download Report
                </Button>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" />
                <ModalContent borderRadius="2xl" shadow="dark-lg" bg={modalBg} overflow="hidden">
                    <ModalHeader
                        bgGradient="linear(to-r, teal.600, blue.600)"
                        color="white"
                        p={6}
                        position="relative"
                    >
                        <HStack spacing={3} align="center" justify="center">
                            <Box p={2} bg="whiteAlpha.200" borderRadius="lg" backdropFilter="blur(10px)">
                                <DownloadIcon boxSize={6} />
                            </Box>
                            <Text fontSize="2xl" fontWeight="bold" letterSpacing="wide">Download Report</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" top={6} right={6} size="lg" />

                    <ModalBody p={6}>
                        <VStack spacing={5}>
                            {/* Date Selection Card */}
                            <Box
                                w="full"
                                bg={cardBg}
                                p={5}
                                borderRadius="xl"
                                shadow="sm"
                                border="1px"
                                borderColor={borderColor}
                            >
                                <FormControl>
                                    <HStack mb={4} justify="space-between">
                                        <FormLabel fontWeight="bold" color="gray.600" m={0} display="flex" alignItems="center">
                                            <Icon as={CalendarIcon} mr={2} color={accentColor} />
                                            Date Range
                                        </FormLabel>
                                        <Tag size="sm" colorScheme="orange" variant="solid" borderRadius="full">Max 30 Days</Tag>
                                    </HStack>

                                    <Flex gap={2} mb={5} wrap="wrap">
                                        {[
                                            { label: "Today", value: "today" },
                                            { label: "Last Week", value: "lastWeek" },
                                            { label: "Last Month", value: "lastMonth" }
                                        ].map((chip) => (
                                            <Button
                                                key={chip.value}
                                                size="sm"
                                                variant={activeChip === chip.value ? "solid" : "outline"}
                                                colorScheme="teal"
                                                borderRadius="full"
                                                onClick={() => handleDateChipClick(chip.value as any)}
                                                flex="1"
                                                fontWeight="medium"
                                                borderWidth={activeChip === chip.value ? 0 : "1px"}
                                            >
                                                {chip.label}
                                            </Button>
                                        ))}
                                    </Flex>

                                    <SimpleGrid columns={2} spacing={3}>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} ml={1}>Start Date</Text>
                                            <Datepicker
                                                label=""
                                                date={localStartDate}
                                                onDateChange={setLocalStartDate}
                                            />
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1} ml={1}>End Date</Text>
                                            <Datepicker
                                                label=""
                                                date={localEndDate}
                                                onDateChange={setLocalEndDate}
                                            />
                                        </Box>
                                    </SimpleGrid>
                                </FormControl>
                            </Box>

                            {/* Filters Card */}
                            <Box w="full">
                                <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wider">
                                    Filters
                                </Text>
                                <SimpleGrid columns={1} spacing={3}>
                                    <HStack
                                        as={Button}
                                        variant="outline"
                                        w="full"
                                        justifyContent="space-between"
                                        h="auto"
                                        p={4}
                                        borderRadius="xl"
                                        borderColor={isPendingToSubmit ? "teal.400" : borderColor}
                                        bg={isPendingToSubmit ? "teal.50" : cardBg}
                                        _hover={{ borderColor: "teal.400", bg: "teal.50" }}
                                        onClick={() => setIsPendingToSubmit(!isPendingToSubmit)}
                                        role="group"
                                    >
                                        <HStack>
                                            <Box p={2} bg={isPendingToSubmit ? "teal.100" : "gray.100"} borderRadius="md" color={isPendingToSubmit ? "teal.600" : "gray.500"}>
                                                <TimeIcon boxSize={4} />
                                            </Box>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="semibold" color={isPendingToSubmit ? "teal.700" : "gray.700"}>Pending to Submit</Text>
                                                <Text fontSize="xs" color="gray.500">Includes Pending To Submit Documents</Text>
                                            </VStack>
                                        </HStack>
                                        <Checkbox isChecked={isPendingToSubmit} colorScheme="teal" isReadOnly pointerEvents="none" size="lg" />
                                    </HStack>

                                    <HStack
                                        as={Button}
                                        variant="outline"
                                        w="full"
                                        justifyContent="space-between"
                                        h="auto"
                                        p={4}
                                        borderRadius="xl"
                                        borderColor={isDuplicate ? "teal.400" : borderColor}
                                        bg={isDuplicate ? "teal.50" : cardBg}
                                        _hover={{ borderColor: "teal.400", bg: "teal.50" }}
                                        onClick={() => setIsDuplicate(!isDuplicate)}
                                        role="group"
                                    >
                                        <HStack>
                                            <Box p={2} bg={isDuplicate ? "teal.100" : "gray.100"} borderRadius="md" color={isDuplicate ? "teal.600" : "gray.500"}>
                                                <CopyIcon boxSize={4} />
                                            </Box>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="semibold" color={isDuplicate ? "teal.700" : "gray.700"}>Duplicate Documents</Text>
                                                <Text fontSize="xs" color="gray.500">Includes Duplicate Documents</Text>
                                            </VStack>
                                        </HStack>
                                        <Checkbox isChecked={isDuplicate} colorScheme="teal" isReadOnly pointerEvents="none" size="lg" />
                                    </HStack>
                                </SimpleGrid>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter p={6} bg={useColorModeValue("gray.50", "gray.900")} borderTopWidth="1px">
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={onClose}
                            size="lg"
                            w="full"
                            colorScheme="gray"
                        >
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            size="lg"
                            w="full"
                            isLoading={reportLoading}
                            onClick={downloadExcelReport}
                            shadow="lg"
                            bgGradient="linear(to-r, teal.500, blue.500)"
                            _hover={{
                                bgGradient: "linear(to-r, teal.600, blue.600)",
                                shadow: "xl",
                                transform: "translateY(-1px)",
                            }}
                            leftIcon={<DownloadIcon />}
                        >
                            Download
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default DownloadReportElement;
